import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  EditableSlotGroup,
  RichTextContent,
  useSlotGroup,
} from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { btnPrimary, hasValue, linkTarget } from '../utils/content';

const tallDef: SlotGroupDefinition = {
  label: 'Tall Photo (left)',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[9].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[9].alt_text' },
  },
  sourceBinding: { collection: 'photo_gallery', defaultIndex: 9, fieldMappings: { image: 'image', alt: 'alt_text' } },
  generator: {
    prompt: 'A lifestyle photo of people enjoying the surrounding area or an outdoor activity typical of this location.',
    context: 'Tall left tile of a 4-photo lifestyle mosaic on the home page. Do not reuse tiles 2–4.',
  },
};

const smallOneDef: SlotGroupDefinition = {
  label: 'Small Photo 1 (top)',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[10].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[10].alt_text' },
  },
  sourceBinding: { collection: 'photo_gallery', defaultIndex: 10, fieldMappings: { image: 'image', alt: 'alt_text' } },
  generator: {
    prompt: 'A candid lifestyle or community photo with people.',
    context: 'Top-middle tile of a 4-photo lifestyle mosaic on the home page. Different subject from the other tiles.',
  },
};

const smallTwoDef: SlotGroupDefinition = {
  label: 'Small Photo 2 (top)',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[11].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[11].alt_text' },
  },
  sourceBinding: { collection: 'photo_gallery', defaultIndex: 11, fieldMappings: { image: 'image', alt: 'alt_text' } },
  generator: {
    prompt: 'A relaxed at-home or work-from-home lifestyle photo.',
    context: 'Top-right tile of a 4-photo lifestyle mosaic on the home page. Different subject from the other tiles.',
  },
};

const wideDef: SlotGroupDefinition = {
  label: 'Wide Photo (bottom)',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[12].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[12].alt_text' },
  },
  sourceBinding: { collection: 'photo_gallery', defaultIndex: 12, fieldMappings: { image: 'image', alt: 'alt_text' } },
  generator: {
    prompt: 'A photo of nearby dining, nightlife, or social life in the neighborhood.',
    context: 'Wide bottom-right tile of a 4-photo lifestyle mosaic on the home page. Different subject from the other tiles.',
  },
};

const ctaDef: SlotGroupDefinition = {
  label: 'Gallery Button',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/gallery' },
    label: { label: 'Button Text', type: 'text', defaultValue: 'Photo Gallery' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

function Tile({ image, alt, sizes, delay }: { image: string | null; alt: string | null; sizes: string; delay: string }) {
  return (
    <div className={`js-reveal-img js-zoom ${delay} absolute inset-0 overflow-hidden bg-muted`}>
      <VibesitesImage
        url={image || ''}
        alt={hasValue(alt) ? alt : ''}
        sizes={sizes}
        className="h-full w-full object-cover"
      />
    </div>
  );
}

export function GalleryHighlight() {
  const tall = useSlotGroup('galleryhighlight.tall', tallDef);
  const smallOne = useSlotGroup('galleryhighlight.small_1', smallOneDef);
  const smallTwo = useSlotGroup('galleryhighlight.small_2', smallTwoDef);
  const wide = useSlotGroup('galleryhighlight.wide', wideDef);
  const cta = useSlotGroup('galleryhighlight.cta', ctaDef);

  return (
    <section className="bg-background pb-14 pt-10 md:pb-16 md:pt-14">
      <div className="mx-auto max-w-[1440px] px-1.5">
        <div className="grid grid-cols-2 gap-1.5 md:h-[min(62vw,600px)] md:grid-cols-[4fr_3fr_3fr] md:grid-rows-[minmax(0,37fr)_minmax(0,63fr)]">
          <div className="relative min-h-0 col-span-2 aspect-[4/3] md:col-span-1 md:row-span-2 md:aspect-auto">
            <EditableSlotGroup groupPath="galleryhighlight.tall" definition={tallDef} className="h-full">
              <Tile image={tall.image} alt={tall.alt} sizes="(min-width: 768px) 40vw, 100vw" delay="" />
            </EditableSlotGroup>
          </div>
          <div className="relative min-h-0 aspect-square md:aspect-auto">
            <EditableSlotGroup groupPath="galleryhighlight.small_1" definition={smallOneDef} className="h-full">
              <Tile image={smallOne.image} alt={smallOne.alt} sizes="(min-width: 768px) 30vw, 50vw" delay="js-d1" />
            </EditableSlotGroup>
          </div>
          <div className="relative min-h-0 aspect-square md:aspect-auto">
            <EditableSlotGroup groupPath="galleryhighlight.small_2" definition={smallTwoDef} className="h-full">
              <Tile image={smallTwo.image} alt={smallTwo.alt} sizes="(min-width: 768px) 30vw, 50vw" delay="js-d2" />
            </EditableSlotGroup>
          </div>
          <div className="relative min-h-0 col-span-2 aspect-[16/9] md:aspect-auto">
            <EditableSlotGroup groupPath="galleryhighlight.wide" definition={wideDef} className="h-full">
              <Tile image={wide.image} alt={wide.alt} sizes="(min-width: 768px) 60vw, 100vw" delay="js-d3" />
            </EditableSlotGroup>
          </div>
        </div>
      </div>

      <div className="mx-auto mt-12 max-w-[760px] px-6 text-center md:mt-14">
        <RichTextContent path="galleryhighlight.body" as="div" className="js-reveal js-copy text-foreground" />
        <EditableSlotGroup groupPath="galleryhighlight.cta" definition={ctaDef} className="js-reveal js-d1">
          <a href={cta.url || '/gallery'} target={linkTarget(cta.url, cta._target)} className={`${btnPrimary} mt-8`}>
            {cta.label || 'Photo Gallery'}
          </a>
        </EditableSlotGroup>
      </div>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'GalleryHighlight',
  displayName: 'Lifestyle Mosaic',
  tokens: {
    'galleryhighlight.body': {
      label: 'Gallery Intro',
      type: 'richtext',
      defaultField: 'property_home_gallery_blurb',
      suggestedFields: ['property_home_gallery_blurb', 'property_gallery_blurb'],
      defaultValue:
        '<p>At <span data-type="field-token" data-field="property_name"></span>, room to breathe meets room to connect. Explore our photo gallery to see spacious homes, creative gathering spots, and design-forward spaces that bring the community together.</p>',
    },
  },
  slotGroups: {
    'galleryhighlight.tall': tallDef,
    'galleryhighlight.small_1': smallOneDef,
    'galleryhighlight.small_2': smallTwoDef,
    'galleryhighlight.wide': wideDef,
    'galleryhighlight.cta': ctaDef,
  },
};

(GalleryHighlight as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default GalleryHighlight;
